// src/pages/addon-services/AddonServiceForm.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  TextField, 
  Button, 
  Container, 
  Box, 
  Typography, 
  FormControlLabel, 
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Avatar,
  SelectChangeEvent,
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { getAddonServiceById, createAddonService, updateAddonService } from 'services/serviceService';
import { AddonService } from 'types/service';

type AddonServiceFormData = {
  name: string;
  description: string;
  base_price: string;
  duration: string;
  status: boolean;
  category: string;
  display_order: string;
  image: File | null;
  imagePreview: string;
};

const AddonServiceForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<AddonServiceFormData>({
    name: '',
    description: '',
    base_price: '0',
    duration: '30',
    status: true,
    category: 'other',
    display_order: '0',
    image: null,
    imagePreview: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAddonServiceById = useCallback(async (serviceId: number) => {
    const addonService = await getAddonServiceById(serviceId);
    return addonService;
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        if (id) {
          const addonService = await fetchAddonServiceById(parseInt(id));
          setFormData(prev => ({
            ...prev,
            name: addonService.name || '',
            description: addonService.description || '',
            base_price: addonService.base_price?.toString() || '0',
            duration: addonService.duration?.toString() || '30',
            status: addonService.status !== undefined ? Boolean(addonService.status) : true,
            category: addonService.category || 'other',
            display_order: addonService.display_order?.toString() || '0',
            imagePreview: addonService.image_url || ''
          }));
        } else {
          // Reset form for new addon service
          setFormData({
            name: '',
            description: '',
            base_price: '0',
            duration: '30',
            status: true,
            category: 'other',
            display_order: '0',
            image: null,
            imagePreview: ''
          });
        }
      } catch (error) {
        console.error('Error fetching addon service:', error);
        setError('Failed to load addon service data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, fetchAddonServiceById]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData(prev => ({
        ...prev,
        image: file,
        imagePreview: URL.createObjectURL(file)
      }));
    }
  };

  const handleNumberInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Only allow numbers and decimal for base_price
    if ((name === 'base_price' && /^\d*\.?\d{0,2}$/.test(value)) || 
        (name !== 'base_price' && /^\d*$/.test(value))) {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }> | 
       SelectChangeEvent<unknown>
  ) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type } = target;
    const checked = target.checked;
    
    setFormData(prev => ({
      ...prev,
      [name as string]: type === 'checkbox' ? checked : value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      // Validate required fields
      if (!formData.name?.trim()) {
        alert('Name is required');
        return;
      }
      if (!formData.base_price) {
        alert('Price is required');
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('description', formData.description?.trim() || '');
      formDataToSend.append('price', formData.base_price);
      formDataToSend.append('duration', formData.duration || '0');
      formDataToSend.append('status', formData.status ? '1' : '0');
      formDataToSend.append('display_order', formData.display_order || '0');
      formDataToSend.append('category', formData.category || 'other');

      // For now, let's skip the image upload to simplify the process
      // if (formData.image) {
      //   formDataToSend.append('image', formData.image);
      // }

      if (id) {
        await updateAddonService(parseInt(id), formDataToSend);
      } else {
        await createAddonService(formDataToSend);
      }

      // Show success message
      alert('Addon service saved successfully!');
      navigate('/addon-services');
    } catch (error: unknown) {
      console.error('Error saving addon service:', error);
      // Show the actual error message from the server if available
      const errorMessage = error instanceof Error ? error.message : 'Failed to save addon service. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Function to populate form with test data
  const fillTestData = () => {
    setFormData(prev => ({
      ...prev,
      name: 'Premium Detailing',
      description: 'Complete interior and exterior detailing',
      base_price: '79.99',
      duration: '120',
      status: true,
      category: 'detailing',
      display_order: '1',
      image: null,
      imagePreview: ''
    }));
  };

  return (
    <Container maxWidth={false} sx={{ maxWidth: '100%', px: 0 }}>
      <Box sx={{ mt: 0, width: '100%' }}>
        <Paper elevation={3} sx={{ p: 3,  width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" gutterBottom>
              {id ? 'Edit' : 'Add New'} Addon Service
            </Typography>
            {/*process.env.NODE_ENV === 'development' && !id && (
              <Button 
                variant="outlined" 
                color="secondary"
                onClick={fillTestData}
                size="small"
              >
                Fill Test Data
              </Button>
            )*/}
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Loading Spinner */}
          {loading && (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress />
            </Box>
          )}

          {!loading && <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              margin="normal"
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={loading}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={3}
              disabled={loading}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Base Price"
              name="base_price"
              value={formData.base_price}
              onChange={handleNumberInput}
              required
              inputProps={{ min: "0", step: "0.01" }}
              disabled={loading}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Duration (minutes)"
              name="duration"
              type="number"
              value={formData.duration}
              onChange={handleNumberInput}
              required
              inputProps={{ min: "0" }}
              disabled={loading}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Display Order"
              name="display_order"
              type="number"
              value={formData.display_order}
              onChange={handleNumberInput}
              required
              inputProps={{ min: "0" }}
              disabled={loading}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Category</InputLabel>
              <Select
                name="category"
                value={formData.category}
                onChange={handleChange}
                label="Category"
                required
                disabled={loading}
              >
                <MenuItem value="protection">Protection</MenuItem>
                <MenuItem value="polishing">Polishing</MenuItem>
                <MenuItem value="detailing">Detailing</MenuItem>
                <MenuItem value="coating">Coating</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ mt: 2, mb: 2 }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="image-upload"
                type="file"
                onChange={handleImageChange}
                disabled={loading}
              />
              <label htmlFor="image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  sx={{ mb: 1 }}
                  fullWidth
                  disabled={loading}
                >
                  Upload Image
                </Button>
              </label>
              {formData.imagePreview && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Avatar
                    src={formData.imagePreview}
                    alt="Preview"
                    sx={{ width: 100, height: 100, margin: '0 auto' }}
                    variant="rounded"
                  />
                </Box>
              )}
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.status}
                  onChange={handleChange}
                  name="status"
                  color="primary"
                  disabled={loading}
                />
              }
              label="Active"
            />
            <Box sx={{ mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ mr: 2 }}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : null}
              >
                {id ? 'Update' : 'Create'} Addon Service
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/addon-services')}
                disabled={loading}
              >
                Cancel
              </Button>
            </Box>
          </form>}
        </Paper>
      </Box>
    </Container>
  );
};

export default AddonServiceForm;